import React, { useEffect, useState } from 'react';
import {
  // TextField,
  EditButton,
  CreateButton,
  useDataProvider,
  useNotify,
  useTranslate,
} from 'react-admin';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Alert,
  CircularProgress,
  // Divider,
  Chip,
  Paper,
  Container,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon 
} from '@mui/icons-material';

interface SingleRecordListProps {
  resource: string;
  title?: string;
  emptyMessage?: string;
  createButtonLabel?: string;
  editButtonLabel?: string;
  fields?: React.ReactNode[];
  actions?: React.ReactNode;
}

const SingleRecordActions = ({ 
  resource, 
  createButtonLabel, 
  editButtonLabel,
  hasRecord,
  recordId 
}: {
  resource: string;
  createButtonLabel?: string;
  editButtonLabel?: string;
  hasRecord: boolean;
  recordId?: string;
}) => {
  const translate = useTranslate();

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {!hasRecord ? (
        <CreateButton
          resource={resource}
          label={createButtonLabel || translate('ra.action.create')}
          variant="contained"
          color="primary"
          size="large"
          startIcon={<AddIcon />}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1.5
          }}
        />
      ) : (
        <EditButton
          resource={resource}
          record={{ id: recordId } as any}
          label={editButtonLabel || translate('ra.action.edit')}
          variant="contained"
          color="primary"
          size="large"
          startIcon={<EditIcon />}
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1.5
          }}
        />
      )}
    </Box>
  );
};

const SingleRecordContent = ({
  resource,
  title,
  emptyMessage,
  createButtonLabel,
  editButtonLabel,
  // fields,
}: SingleRecordListProps) => {
  const [record, setRecord] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();

  useEffect(() => {
    loadRecord();
  }, [resource]);

  const loadRecord = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try to get the first (and should be only) record
      const response = await dataProvider.getList(resource, {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: {},
      });
      
      if (response.data.length > 0) {
        setRecord(response.data[0]);
      } else {
        setRecord(null);
      }
    } catch (err: any) {
      console.error('Error loading record:', err);
      setError(err.message || 'Failed to load record');
      notify('Ошибка загрузки данных', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 3
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
              {title || translate(`resources.${resource}.name`, { smart_count: 1 })}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              {record ? 'Управление содержимым страницы' : 'Создайте содержимое для этой страницы'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {record && (
              <Chip 
                icon={<CheckCircleIcon />} 
                label="Активно" 
                color="success" 
                variant="filled"
                sx={{ 
                  backgroundColor: 'rgba(76, 175, 80, 0.2)',
                  color: 'white',
                  border: '1px solid rgba(76, 175, 80, 0.5)'
                }}
              />
            )}
            <SingleRecordActions
              resource={resource}
              createButtonLabel={createButtonLabel}
              editButtonLabel={editButtonLabel}
              hasRecord={!!record}
              recordId={record?.id}
            />
          </Box>
        </Box>
      </Paper>

      {/* Content */}
      {!record ? (
        <Card 
          elevation={2} 
          sx={{ 
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid rgba(0, 0, 0, 0.06)'
          }}
        >
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <InfoIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            </Box>
            
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Содержимое не найдено
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
              {emptyMessage || 'Для этой страницы еще не создано содержимое. Нажмите кнопку ниже, чтобы создать его.'}
            </Typography>
            
            <CreateButton
              resource={resource}
              label={createButtonLabel || translate('ra.action.create')}
              variant="contained"
              color="primary"
              size="large"
              startIcon={<AddIcon />}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                fontSize: '1.1rem'
              }}
            />
          </CardContent>
        </Card>
      ) : (
        <Card 
          elevation={2} 
          sx={{ 
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid rgba(0, 0, 0, 0.06)'
          }}
        >
          <CardHeader 
            title="Содержимое страницы"
            subheader={`Последнее обновление: ${new Date(record.updatedAt).toLocaleString('ru-RU')}`}
            sx={{ 
              backgroundColor: 'grey.50',
              borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
            }}
          />
          
          {/*<CardContent sx={{ p: 4 }}>*/}
          {/*  /!* Display record fields *!/*/}
          {/*  <Box sx={{ mb: 4 }}>*/}
          {/*    {fields ? (*/}
          {/*      <Box sx={{ */}
          {/*        '& > *': { mb: 3 },*/}
          {/*        '& .RaTextField-root': {*/}
          {/*          '& .MuiTypography-root': {*/}
          {/*            fontSize: '1rem',*/}
          {/*            lineHeight: 1.6*/}
          {/*          }*/}
          {/*        }*/}
          {/*      }}>*/}
          {/*        {React.Children.map(fields, (field) =>*/}
          {/*          React.cloneElement(field as React.ReactElement, { record })*/}
          {/*        )}*/}
          {/*      </Box>*/}
          {/*    ) : (*/}
          {/*      <Box sx={{ */}
          {/*        display: 'grid',*/}
          {/*        gap: 2,*/}
          {/*        '& .RaTextField-root': {*/}
          {/*          '& .MuiTypography-root': {*/}
          {/*            fontSize: '1rem'*/}
          {/*          }*/}
          {/*        }*/}
          {/*      }}>*/}
          {/*        <TextField source="id" record={record} />*/}
          {/*        <TextField source="createdAt" record={record} />*/}
          {/*        <TextField source="updatedAt" record={record} />*/}
          {/*      </Box>*/}
          {/*    )}*/}
          {/*  </Box>*/}
          {/*  */}
          {/*  <Divider sx={{ my: 3 }} />*/}
          {/*  */}
          {/*  /!* Actions *!/*/}
          {/*  <Box display="flex" justifyContent="center" gap={2}>*/}
          {/*    <EditButton*/}
          {/*      resource={resource}*/}
          {/*      record={record}*/}
          {/*      label={editButtonLabel || translate('ra.action.edit')}*/}
          {/*      variant="contained"*/}
          {/*      color="primary"*/}
          {/*      size="large"*/}
          {/*      startIcon={<EditIcon />}*/}
          {/*      sx={{ */}
          {/*        borderRadius: 2,*/}
          {/*        textTransform: 'none',*/}
          {/*        fontWeight: 600,*/}
          {/*        px: 4,*/}
          {/*        py: 1.5,*/}
          {/*        fontSize: '1.1rem'*/}
          {/*      }}*/}
          {/*    />*/}
          {/*  </Box>*/}
          {/*</CardContent>*/}
        </Card>
      )}
    </Container>
  );
};

export const SingleRecordList: React.FC<SingleRecordListProps> = (props) => {
  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: 'grey.50',
      pt: 0
    }}>
      <SingleRecordContent {...props} />
    </Box>
  );
};
